import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../../../components-v2/Button';
import { ButtonBase } from '../../../../../../../components/Button';
import { AudienceMode } from '../../hooks/useCampaignAudiences';
import { WarningBanner, WarningBannerMessage } from '../../../../../presentation';
import { styleSheet } from './styles';

interface ICard {
	value: AudienceMode;
	title: string;
	description: string;
}

const CARDS: ICard[] = [
	{
		value: 'same',
		title: 'Same recipients for everything',
		description: 'Pick one recipient list in the next step. Every scheduled email goes to the same people.',
	},
	{
		value: 'per',
		title: 'Set audience per campaign',
		description: 'Give each email its own audience from your contact tags. Best when pieces target different segments.',
	},
];

export function AudienceModeScreen(props: {
	emailCount: number;
	mode: AudienceMode;
	onModeChange: (mode: AudienceMode) => void;
	onContinue: () => void;
}): JSX.Element {
	const { emailCount, mode, onModeChange, onContinue } = props;

	return (
		<div className={css(styleSheet.audienceModeScreenContainer)}>
			<div className={css(styleSheet.audienceModeScreenLeft)}>
				<p className={css(styleSheet.audienceModeScreenHeading)}>Who receives these campaigns?</p>
				<p className={css(styleSheet.audienceModeScreenSubhead)}>
					{"You're scheduling "}
					<strong>{emailCount} emails</strong>
					{'. Choose how audiences work for this batch — you set this once, up front.'}
				</p>
				<WarningBanner>
					<WarningBannerMessage>
						{'This is a one-time choice for the batch. '}
						<strong>Switching modes later resets any audience selections you&apos;ve made.</strong>
					</WarningBannerMessage>
				</WarningBanner>
			</div>
			<div className={css(styleSheet.audienceModeScreenRight)}>
				<div role='radiogroup' aria-label='Audience mode' className={css(styleSheet.audienceModeScreenCards)}>
					{CARDS.map(card => {
						const selected = mode === card.value;
						return (
							<ButtonBase
								key={card.value}
								role='radio'
								aria-checked={selected}
								aria-label={card.title}
								className={css(
									styleSheet.audienceModeScreenCard,
									selected && styleSheet.audienceModeScreenCardSelected
								)}
								onClick={() => onModeChange(card.value)}
							>
								<div className={css(styleSheet.audienceModeScreenCardHeader)}>
									<div
										className={css(
											styleSheet.audienceModeScreenRadioDot,
											selected && styleSheet.audienceModeScreenRadioDotSelected
										)}
									>
										{selected && <div className={css(styleSheet.audienceModeScreenRadioInner)} />}
									</div>
									<p className={css(styleSheet.audienceModeScreenCardTitle)}>{card.title}</p>
								</div>
								<p className={css(styleSheet.audienceModeScreenCardDescription)}>{card.description}</p>
							</ButtonBase>
						);
					})}
				</div>
				<Button variant='primary' size='lg' styles={[styleSheet.audienceModeScreenContinue]} onClick={onContinue}>
					Continue
				</Button>
			</div>
		</div>
	);
}
